import React, { useMemo } from "react";
import { connect } from "react-redux";
import { createStore } from 'devextreme-aspnet-data-nojquery';
import DataSource from "devextreme/data/data_source";
import {
    Column,
    DataGrid,
    Export,
    ColumnChooser,
    ColumnChooserSearch,
    FilterRow,
    Sorting,
    Paging,
    Pager,
    Selection,
} from "devextreme-react/data-grid";
import { IDCustomerLookupLayout } from "../../../common/lookups/Layouts/IdLayouts";
import { GRID_PAGE_SIZES } from "./constants";

// Reuses the same common/Lookup/GetLookup (module=id, lookupType=Customer) query that already
// powers every R_CUSTOMER lookup edit in the app (IDCustomerLookupLayout), instead of the bare
// Id+Name id/Lookup/GetCustomers endpoint - that one only returns two fields and can't
// reproduce ARD0009's grdTopCustomer.
//
// The COLUMNS, though, are mapped here off the endpoint's actual payload rather than taken
// from IDCustomerLookupLayout's own column list. That list is written for the compact dropdown
// picker and several of its dataFields don't match the casing GetLookup really returns
// ("customername" vs the payload's "customerName", "cellphone1" vs "cellPhone1",
// "emailaddress1" vs "emailAddress1"). DevExtreme matches dataField case-sensitively, so those
// columns bind to nothing and render blank - which is why Name was empty in this grid.
//
// Visible set is ARD0009's grdTopCustomer: Customer, Name, Address-1, Address-2, City, State,
// Zip, Salesperson, Telephone. Salesperson is IDLookupService.HandleCustomerLookup's own
// correlated-subquery match of qryCustomerRead's "first entered Salesperson participant"
// computed column. The rest stay reachable through the column chooser.
const CUSTOMER_COLUMNS = [
    { dataField: 'identityid', caption: 'Customer', dataType: 'string' },
    { dataField: 'customerName', caption: 'Name', dataType: 'string' },
    { dataField: 'address1', caption: 'Address - 1', dataType: 'string' },
    { dataField: 'address2', caption: 'Address - 2', dataType: 'string' },
    { dataField: 'city', caption: 'City', dataType: 'string' },
    { dataField: 'state', caption: 'State', dataType: 'string' },
    { dataField: 'zip', caption: 'Zip', dataType: 'string' },
    { dataField: 'salesperson', caption: 'Salesperson', dataType: 'string' },
    { dataField: 'telephone1', caption: 'Telephone', dataType: 'string' },

    // Column chooser (hidden by default).
    { dataField: 'recordNumber', caption: 'Record #', dataType: 'string', visible: false },
    { dataField: 'country', caption: 'Country', dataType: 'string', visible: false },
    { dataField: 'telephone2', caption: 'Telephone - 2', dataType: 'string', visible: false },
    { dataField: 'fax1', caption: 'Fax', dataType: 'string', visible: false },
    { dataField: 'cellPhone1', caption: 'Cell Phone', dataType: 'string', visible: false },
    { dataField: 'emailAddress1', caption: 'E-Mail', dataType: 'string', visible: false },
    { dataField: 'contactname', caption: 'Contact Name', dataType: 'string', visible: false },
    { dataField: 'creditlimit', caption: 'Credit Limit', dataType: 'number', format: '#,##0.00', visible: false },
    { dataField: 'status', caption: 'Status', dataType: 'string', visible: false },
    { dataField: 'creationdatetime', caption: 'Creation Date', dataType: 'datetime', format: 'MM/dd/yyyy hh:mm:ss a', visible: false },
    { dataField: 'lastupdatedatetime', caption: 'Last Updated', dataType: 'datetime', format: 'MM/dd/yyyy hh:mm:ss a', visible: false },

    { dataField: 'id', caption: 'Row Id', visible: false, showInColumnChooser: false },
];

// Selecting a row calls onCustomerChanged immediately (no "start" action needed) - matches
// ARD0009's Customer tab, where picking a row directly drives the bottom Enter/Review panel.
const CustomerBrowseGrid = (props) => {
    const accessToken = props.oidc?.user?.access_token;

    const layout = useMemo(() => IDCustomerLookupLayout(), []);

    const dataSource = useMemo(() => new DataSource({
        store: createStore({
            key: layout.valueExpr,
            loadUrl: `${window._env_.REACT_APP_APIS}/common/Lookup/GetLookup`,
            loadParams: layout.params,
            onBeforeSend: (method, ajaxOptions) => {
                ajaxOptions.headers = {
                    access_token: accessToken,
                    Authorization: `Bearer ${accessToken}`,
                };
            },
        }),
        sort: layout.sort,
    }), [accessToken, layout]);

    const onSelectionChanged = ({ selectedRowsData }) => {
        const customer = selectedRowsData && selectedRowsData.length === 1 ? selectedRowsData[0] : null;
        props.onCustomerChanged(customer);
    };

    return (
        <DataGrid
            dataSource={dataSource}
            // Fills whatever definite-height box the caller wraps this in (e.g. a
            // flex item with a fixed height/flex-basis) - unlike the app's
            // "search-page-grid" convention, this component isn't always a direct
            // child of the full page height chain, so it can't rely on that class.
            height="100%"
            allowColumnReordering={true}
            allowColumnResizing={true}
            showBorders={true}
            hoverStateEnabled={true}
            onSelectionChanged={onSelectionChanged}
            remoteOperations={true}
            searchEnabled={true}
            columnAutoWidth={true}
        >
            <ColumnChooser enabled={true} title="Choose Column" width={290} height={290} sortOrder="asc">
                <ColumnChooserSearch enabled={true} editorOptions={{ placeholder: 'Search column' }} />
            </ColumnChooser>
            <Export enabled={true} />
            <Selection mode="single" />
            <Sorting visible={true} mode="multiple" />
            <Paging defaultPageSize={10} />
            <Pager visible={true} showPageSizeSelector={true} allowedPageSizes={GRID_PAGE_SIZES} showNavigationButtons={true} />
            <FilterRow visible={true} />
            {CUSTOMER_COLUMNS.map((column) => (
                <Column key={column.dataField} {...column} />
            ))}
        </DataGrid>
    );
};

function mapStateToProps(state) {
    return {
        oidc: state.oidc,
    };
}

export default connect(mapStateToProps)(CustomerBrowseGrid);
